/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, Bug, RefreshCw, Search, UserCheck, UserX, Undo2, Loader2, Fingerprint, FlaskConical, Mail, BadgeCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PurgeQuarantineDialog } from '@/components/admin/PurgeQuarantineDialog';
import {
  getFraudReport, runFraudScan, setFraudStatus, validateCodiceFiscale, validatePartitaIva, validateEmail, analyzeProfileRemote, STATUS_META, FLAG_LABEL,
} from '@/lib/antiFraud';

/**
 * ValidationPage — /admin/validation
 * Report anti-frode: contatori, elenco account in quarantena (SUSPECTED / TEST_ACCOUNT),
 * override manuale, riesame globale e validatore live CF / P.IVA / email.
 */

const Kpi = ({ icon: Icon, label, value, tone, testId }) => (
  <Card className="rounded-xl" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0', tone === 'ok' ? 'bg-success/10 text-success' : tone === 'warn' ? 'bg-warning/10 text-warning' : tone === 'bad' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')}><Icon className="h-5 w-5" /></div>
      <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div><div className="font-display font-bold text-2xl leading-tight">{value}</div></div>
    </CardContent>
  </Card>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { dateStyle: 'medium' }) : '—');

export default function ValidationPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState('QUARANTINE');
  const [q, setQ] = useState('');
  const [acting, setActing] = useState(null);
  // validatore live
  const [cf, setCf] = useState('');
  const [piva, setPiva] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [remote, setRemote] = useState(null);
  const [remoteBusy, setRemoteBusy] = useState(false);

  const load = useCallback(async (f = filter) => {
    setLoading(true);
    try { setReport(await getFraudReport(f === 'QUARANTINE' ? { limit: 300 } : { status: f, limit: 300 })); } catch { toast.error('Impossibile caricare il report anti-frode'); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const scan = async () => {
    setScanning(true);
    try { const r = await runFraudScan(false); toast.success(`Riesaminati ${r.scanned} account · ${r.changed} cambi di stato`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Scansione non riuscita'); } finally { setScanning(false); }
  };

  const setStatus = async (u, status) => {
    setActing(u.id + status);
    try {
      await setFraudStatus(u.id, status, status === 'AUTO' ? 'ripristino automatico' : 'override manuale admin');
      toast.success(status === 'AUTO' ? 'Stato ricalcolato' : `${u.full_name || u.email}: ${STATUS_META[status].label}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Operazione non riuscita'); } finally { setActing(null); }
  };

  const items = useMemo(() => {
    const list = report?.items || [];
    const s = q.trim().toLowerCase();
    return s ? list.filter((u) => (u.email || '').toLowerCase().includes(s) || (u.full_name || '').toLowerCase().includes(s)) : list;
  }, [report, q]);

  const cfRes = cf ? validateCodiceFiscale(cf) : null;
  const pivaRes = piva ? validatePartitaIva(piva) : null;
  const emailRes = email ? validateEmail(email) : null;

  const analyzeRemote = async () => {
    setRemoteBusy(true);
    try { setRemote(await analyzeProfileRemote({ full_name: name || null, email: email || null, codice_fiscale: cf || null, piva: piva || null })); } catch { toast.error('Analisi non riuscita'); } finally { setRemoteBusy(false); }
  };

  const counts = report?.counts || {};

  return (
    <div className="space-y-5" data-testid="validation-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><ShieldAlert className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display font-bold text-xl">Anti-frode & validazione</h1>
            <p className="text-xs text-muted-foreground">Account di test e profili sospetti vengono esclusi automaticamente da directory e agenda pubblica.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PurgeQuarantineDialog counts={report?.counts || {}} onDone={() => load()} />
          <Button onClick={scan} disabled={scanning} className="h-10 rounded-xl" data-testid="validation-scan-btn">{scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Riesamina tutti gli account</Button>
        </div>
      </div>

      {loading && !report ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi icon={ShieldCheck} label="Verificati" value={counts.VERIFIED ?? 0} tone="ok" testId="validation-kpi-verified" />
          <Kpi icon={ShieldAlert} label="Sospetti" value={counts.SUSPECTED ?? 0} tone="warn" testId="validation-kpi-suspected" />
          <Kpi icon={Bug} label="Account di test" value={counts.TEST_ACCOUNT ?? 0} tone="bad" testId="validation-kpi-test" />
          <Kpi icon={UserX} label="In quarantena" value={report?.quarantined ?? 0} testId="validation-kpi-quarantined" />
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[2.2fr_1fr]">
        <Card className="rounded-xl" data-testid="validation-list-card">
          <CardHeader className="pb-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div><CardTitle className="text-base">Account segnalati</CardTitle><CardDescription>Motivazioni trasparenti per ogni segnalazione. L'override manuale è tracciato in audit.</CardDescription></div>
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="rounded-xl">
                  <TabsTrigger value="QUARANTINE" className="rounded-lg text-xs" data-testid="validation-tab-quarantine">Quarantena</TabsTrigger>
                  <TabsTrigger value="TEST_ACCOUNT" className="rounded-lg text-xs" data-testid="validation-tab-test">Test</TabsTrigger>
                  <TabsTrigger value="SUSPECTED" className="rounded-lg text-xs" data-testid="validation-tab-suspected">Sospetti</TabsTrigger>
                  <TabsTrigger value="VERIFIED" className="rounded-lg text-xs" data-testid="validation-tab-verified">Verificati</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="relative"><Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtra per nome o email" className="h-10 rounded-xl pl-9" data-testid="validation-search" /></div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
              : items.length === 0 ? <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="validation-empty"><BadgeCheck className="h-8 w-8 mx-auto mb-2 text-success/70" /> Nessun account in questa categoria.</div>
              : (
                <div className="rounded-xl border border-border/70 overflow-hidden">
                  <Table data-testid="validation-table">
                    <TableHeader><TableRow><TableHead className="w-[42%]">Utente</TableHead><TableHead className="w-16">Score</TableHead><TableHead>Segnali</TableHead><TableHead className="text-right w-24">Azioni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {items.map((u) => {
                        const meta = STATUS_META[u.verification_status] || STATUS_META.UNCHECKED;
                        return (
                          <TableRow key={u.id} data-testid={`validation-row-${u.id}`}>
                            <TableCell className="align-top">
                              <div className="font-medium text-sm">{u.full_name || '—'}</div>
                              <div className="text-[11px] text-muted-foreground font-mono break-all">{u.email}</div>
                              <div className="flex gap-1 mt-1 flex-wrap"><Badge variant={meta.variant} className="text-[10px] whitespace-nowrap">{meta.label}</Badge>{u.verification_override && <Badge variant="outline" className="text-[10px]">override</Badge>}<span className="text-[10px] text-muted-foreground whitespace-nowrap">{u.category} · {fmtDate(u.created_at)}</span></div>
                            </TableCell>
                            <TableCell className="align-top"><span className={cn('font-mono font-bold', (u.fraud_score || 0) >= 60 ? 'text-destructive' : (u.fraud_score || 0) >= 30 ? 'text-warning' : 'text-success')}>{u.fraud_score ?? '—'}</span></TableCell>
                            <TableCell className="align-top">
                              <div className="flex flex-wrap gap-1">{(u.fraud_flags || []).slice(0, 4).map((f) => <Badge key={f} variant="secondary" className="text-[10px] font-normal whitespace-nowrap">{FLAG_LABEL(f)}</Badge>)}{(u.fraud_flags || []).length > 4 && <Badge variant="secondary" className="text-[10px]">+{u.fraud_flags.length - 4}</Badge>}{(u.fraud_flags || []).length === 0 && <span className="text-[11px] text-muted-foreground">nessun segnale</span>}</div>
                            </TableCell>
                            <TableCell className="align-top text-right">
                              <TooltipProvider delayDuration={200}>
                                <div className="flex justify-end gap-1">
                                  {u.verification_status !== 'VERIFIED' && <Tooltip><TooltipTrigger asChild><Button size="sm" variant="outline" onClick={() => setStatus(u, 'VERIFIED')} disabled={!!acting} className="h-8 rounded-lg" aria-label="Segna verificato" data-testid={`validation-verify-${u.id}`}>{acting === u.id + 'VERIFIED' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}</Button></TooltipTrigger><TooltipContent>Segna come verificato</TooltipContent></Tooltip>}
                                  {u.verification_status !== 'TEST_ACCOUNT' && <Tooltip><TooltipTrigger asChild><Button size="sm" variant="outline" onClick={() => setStatus(u, 'TEST_ACCOUNT')} disabled={!!acting} className="h-8 rounded-lg text-destructive hover:text-destructive" aria-label="Quarantena" data-testid={`validation-quarantine-${u.id}`}>{acting === u.id + 'TEST_ACCOUNT' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bug className="h-3.5 w-3.5" />}</Button></TooltipTrigger><TooltipContent>Metti in quarantena (test)</TooltipContent></Tooltip>}
                                  {u.verification_override && <Tooltip><TooltipTrigger asChild><Button size="sm" variant="ghost" onClick={() => setStatus(u, 'AUTO')} disabled={!!acting} className="h-8 rounded-lg" aria-label="Ripristina automatico" data-testid={`validation-auto-${u.id}`}><Undo2 className="h-3.5 w-3.5" /></Button></TooltipTrigger><TooltipContent>Torna alla valutazione automatica</TooltipContent></Tooltip>}
                                </div>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
          </CardContent>
        </Card>

        <Card className="rounded-xl" data-testid="validation-tool-card">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /> Validatore live</CardTitle><CardDescription>Checksum ufficiali CF / P.IVA, controllo domini temporanei e analisi del profilo.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {[
              ['Codice fiscale', cf, setCf, cfRes, 'RSSMRA80A01H501U', 'validation-tool-cf', Fingerprint],
              ['Partita IVA', piva, setPiva, pivaRes, '00743110157', 'validation-tool-piva', BadgeCheck],
              ['Email', email, setEmail, emailRes, 'nome@dominio.it', 'validation-tool-email', Mail],
            ].map(([label, val, set, res, ph, tid, Icon]) => (
              <div key={tid}>
                <Label className="text-xs flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</Label>
                <Input value={val} onChange={(e) => set(e.target.value)} placeholder={ph} className="h-10 rounded-xl font-mono uppercase" data-testid={tid} />
                {res && <p className={cn('text-[11px] mt-1', res.valid ? 'text-success' : 'text-destructive')} data-testid={`${tid}-result`}>{res.valid ? 'Valido' : res.reason}{res.valid && res.sex ? ` · sesso ${res.sex}` : ''}</p>}
              </div>
            ))}
            <div><Label className="text-xs">Nome completo</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" className="h-10 rounded-xl" data-testid="validation-tool-name" /></div>
            <Button variant="outline" onClick={analyzeRemote} disabled={remoteBusy || (!name && !email && !cf && !piva)} className="h-10 rounded-xl w-full" data-testid="validation-tool-analyze">{remoteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />} Analizza profilo (server)</Button>
            {remote && (
              <div className="rounded-xl border border-border/70 p-3 space-y-2" data-testid="validation-tool-analysis">
                <div className="flex items-center justify-between"><span className="text-sm font-semibold">Esito</span><Badge variant={STATUS_META[remote.status]?.variant}>{STATUS_META[remote.status]?.label} · {remote.score}</Badge></div>
                <div className="flex flex-wrap gap-1">{remote.flags.length ? remote.flags.map((f) => <Badge key={f} variant="secondary" className="text-[10px] font-normal">{FLAG_LABEL(f)}</Badge>) : <span className="text-[11px] text-muted-foreground">Nessun segnale di anomalia</span>}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
